import { Alert, Chip, Divider, Stack, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import {
    DESTINATION_NOT_IN_MESSENGER_PIGEON,
    MergePlan,
    MergeTarget,
    OrderInProgress,
    PENDING_PAYMENT_STATUS,
    SOURCE_HAS_ORDERS_IN_PROGRESS,
    totalRows,
} from './merge-schemas'

type Props = {
    plan: MergePlan
    /**
     * Create the destination's Messenger Pigeon row. Offered only for
     * {@link DESTINATION_NOT_IN_MESSENGER_PIGEON}, which is the one blocker a
     * person cannot work around: it has no override, and the usual fix is
     * asking somebody to sign in, which can take days.
     */
    onCreateDestinationRecord?: () => void
    creatingDestinationRecord?: boolean
    /** Close off the unpaid orders listed under the orders blocker. */
    onCancelPendingPaymentOrders?: (orderIds: string[]) => void
    cancellingOrders?: boolean
}

/** Money as the order stores it, which is minor units. */
function formatOrderAmount(order: OrderInProgress): string {
    if (order.totalAmount === null) {
        return 'no amount'
    }

    const amount = (order.totalAmount / 100).toFixed(2)
    return order.currency ? `${amount} ${order.currency.toUpperCase()}` : amount
}

function formatOrderDate(createdAt: string | null): string {
    return createdAt ? new Date(createdAt).toLocaleDateString() : 'unknown date'
}

/**
 * The source's unfinished orders, one line each.
 *
 * The point of showing them is that "4 orders still running" reads the same
 * whether it is four abandoned checkouts or four people waiting on notes, and
 * those want opposite responses.
 */
function OrdersInProgressList({ orders }: { orders: OrderInProgress[] }) {
    return (
        <Stack direction="column" gap="0.25em" marginTop="0.5em">
            {orders.map((order) => (
                <Typography key={order.id} variant="caption" component="p">
                    <strong>{order.orderStatus}</strong>
                    {' · '}
                    {order.orderType ?? 'unknown type'}
                    {' · '}
                    {formatOrderAmount(order)}
                    {' · '}
                    {formatOrderDate(order.createdAt)}
                    {order.stripePaymentIntentStatus && (
                        <> {`· payment ${order.stripePaymentIntentStatus}`}</>
                    )}
                    {!order.placedByAccount && <> · assigned as note taker</>}
                </Typography>
            ))}
        </Stack>
    )
}

function TargetList({ targets }: { targets: MergeTarget[] }) {
    return (
        <Stack
            direction="row"
            gap="0.5em"
            flexWrap="wrap"
            justifyContent="flex-start"
        >
            {targets.map((target) => (
                <Chip
                    key={`${target.database}.${target.table}.${target.column}`}
                    size="small"
                    label={`${target.table} (${target.rows})`}
                    title={`${target.database} · ${target.column}`}
                />
            ))}
        </Stack>
    )
}

/**
 * What the button would do, in the order somebody needs to know it: whether it
 * can run at all, then what moves, then what does not.
 *
 * Blocked data is described in the alerts above and deliberately not listed
 * again as chips. Saying the same thing twice in two formats made the panel
 * look like two separate problems.
 */
export default function MergePreview({
    plan,
    onCreateDestinationRecord,
    creatingDestinationRecord,
    onCancelPendingPaymentOrders,
    cancellingOrders,
}: Props) {
    const hasBlockers = plan.blockers.length > 0

    const unpaidOrders = plan.ordersInProgress.filter(
        (order) => order.orderStatus === PENDING_PAYMENT_STATUS
    )
    const otherOrders = plan.ordersInProgress.filter(
        (order) => order.orderStatus !== PENDING_PAYMENT_STATUS
    )

    return (
        <Stack direction="column" gap="0.75em" textAlign="left" maxWidth="34em">
            <Divider />

            <Typography variant="body2">
                Everything below moves from{' '}
                <strong>{plan.source?.email ?? plan.sourceId}</strong> to{' '}
                <strong>{plan.destination?.email ?? plan.destinationId}</strong>
                . The old account is then disabled.
            </Typography>

            {plan.source?.deleted && (
                <Alert severity="info">
                    The old account is already deleted. That is usually fine, it
                    is the duplicate being emptied.
                </Alert>
            )}

            {hasBlockers && (
                <Typography variant="subtitle2" color="error">
                    This cannot run yet
                </Typography>
            )}

            {plan.blockers.map((blocker) => (
                <Alert key={blocker.code} severity="error">
                    {blocker.message}
                    {blocker.overridable && (
                        <Typography variant="caption" component="p">
                            A developer can override this one once it has been
                            agreed (<code>{blocker.code}</code>).
                        </Typography>
                    )}
                    {blocker.code === SOURCE_HAS_ORDERS_IN_PROGRESS && (
                        <>
                            <OrdersInProgressList
                                orders={plan.ordersInProgress}
                            />
                            {unpaidOrders.length > 0 && (
                                <Typography variant="caption" component="p">
                                    {unpaidOrders.length} of these never got
                                    past the checkout, so nothing was paid and
                                    nothing is owed. The payment state shown is
                                    our copy rather than Stripe's, so check
                                    Stripe first if an amount looks like it
                                    might have gone through.
                                </Typography>
                            )}
                            {otherOrders.length > 0 && (
                                <Typography variant="caption" component="p">
                                    {otherOrders.length} of these are further
                                    along and are left alone. Those are a
                                    conversation with the school, not something
                                    to close here.
                                </Typography>
                            )}
                            {unpaidOrders.length > 0 &&
                                onCancelPendingPaymentOrders && (
                                    <LoadingButton
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        sx={{ marginTop: '0.5em' }}
                                        loading={cancellingOrders}
                                        onClick={() =>
                                            onCancelPendingPaymentOrders(
                                                unpaidOrders.map(
                                                    (order) => order.id
                                                )
                                            )
                                        }
                                    >
                                        Cancel the {unpaidOrders.length} unpaid
                                        order(s)
                                    </LoadingButton>
                                )}
                        </>
                    )}
                    {blocker.code === DESTINATION_NOT_IN_MESSENGER_PIGEON &&
                        onCreateDestinationRecord && (
                            <>
                                <Typography variant="caption" component="p">
                                    This happens when an account was created for
                                    somebody and they have never signed in.
                                    Creating the record here does the same thing
                                    their first sign-in would, and they still
                                    need to sign in eventually.
                                </Typography>
                                <LoadingButton
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    sx={{ marginTop: '0.5em' }}
                                    loading={creatingDestinationRecord}
                                    onClick={onCreateDestinationRecord}
                                >
                                    Create the Messenger Pigeon record
                                </LoadingButton>
                            </>
                        )}
                </Alert>
            ))}

            {plan.followUps.map((followUp) => (
                <Alert key={followUp} severity="warning">
                    {followUp}
                </Alert>
            ))}

            <Typography variant="subtitle2">
                What moves: {totalRows(plan.move)} item(s)
            </Typography>
            {plan.move.length > 0 ? (
                <TargetList targets={plan.move} />
            ) : (
                <Typography variant="body2" color="text.secondary">
                    Nothing. The old account holds no content.
                </Typography>
            )}

            {plan.leave.length > 0 && (
                <>
                    <Typography variant="subtitle2">
                        What stays on the old account: {totalRows(plan.leave)}{' '}
                        item(s)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Logins, membership and its own settings. These belong to
                        the account rather than the person, and go when it is
                        disabled. The destination has its own.
                    </Typography>
                    <TargetList targets={plan.leave} />
                </>
            )}

            <Divider />
        </Stack>
    )
}
